import { useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogTitle, Box, Button, Typography } from '@mui/material';
import BluetoothIcon from '@mui/icons-material/Bluetooth';
import UsbIcon from '@mui/icons-material/Usb';
import { useConnectionWizard } from '../hooks/useConnectionWizard';
import { useDeviceService } from '@/contexts/DeviceServiceContext';
import { useDeviceStore } from '@/store/useDeviceStore';

export const ConnectionTypeDialog = () => {
  const { isOpen, connectionType, connectionStatus, close, selectConnectionType, setConnectionStatus, setError, setDeviceHealth } = useConnectionWizard();
  const deviceService = useDeviceService();

  // Guards the select-then-effect handler below against re-running for the same
  // connectionType selection (e.g. when `deviceService` swaps in on the follow-up
  // render, or a StrictMode double-effect). Tracks which type the effect last acted on;
  // cleared whenever connectionType goes back to null (wizard close()/reset()) so the
  // next selection — even of the same transport — starts fresh. Selecting a *different*
  // transport than last time always fires immediately, since the ref won't match.
  //
  // Known, accepted trade-off (see the regression test in ConnectionTypeDialog.test.tsx):
  // re-clicking the *same* transport's button without an intervening type change or a
  // full wizard close/reopen is a no-op, because connectionType's value doesn't change
  // and React's effect-dependency diffing never re-invokes the effect. This applies
  // symmetrically to Cable and Bluetooth now that both go through this effect.
  const handledForTypeRef = useRef<'usb' | 'bluetooth' | null>(null);

  // Shared connect/health-check/error-classification body for both transports. Reads
  // `deviceService` from the enclosing render's closure — by the time the effect below
  // invokes this, App.tsx has already re-rendered with the transport prop matching
  // `connectionType`, and DeviceServiceProvider's memo has already swapped in the
  // matching service instance (USB DeviceService or BleDeviceService). Calling
  // deviceService.connect() directly from an onClick (as Cable used to) instead risks
  // running against a *stale* closure value — e.g. clicking Cable right after Bluetooth
  // was selected would previously invoke the still-BLE-backed instance, popping the
  // Bluetooth picker for a user who clicked "Cable" — hence select-then-effect for both.
  const performConnect = async () => {
    setConnectionStatus('connecting');

    try {
      // Trigger the browser's native device picker (WebUSB or Web Bluetooth)
      const result = await deviceService.connect();

      if (!result.success) {
        if (result.error?.message.includes('No device selected')) {
          // User cancelled the picker
          setConnectionStatus('selecting');
          return;
        }
        throw result.error;
      }

      // Connection successful, proceed to health check
      setConnectionStatus('verifying');

      // Read fresh state directly from the store — the React-hook snapshot (deviceStore)
      // is stale inside async closures and won't reflect changes made by connect().
      const freshStore = useDeviceStore.getState();
      const deviceInfo = freshStore.deviceInfo;
      const deviceState = freshStore.deviceState;

      // Validate health metrics — same thresholds for both transports
      const health = {
        serialNumber: deviceInfo?.serialNumber ?? '0',
        batteryLevel: deviceState.batteryLevel,
        temperature: deviceState.temperature,
        firmwareVersion: deviceInfo?.firmwareVersion ?? '0',
        isReady: true,
      };

      // Check if device is healthy — only fail on values that are explicitly known bad
      // (0 = unknown/not reported yet, treat as OK)
      if (health.batteryLevel > 0 && health.batteryLevel < 10) {
        throw new Error(`Battery too low (${Math.round(health.batteryLevel)}%). Please charge the device.`);
      }

      if (health.temperature !== 0 && (health.temperature > 45 || health.temperature < 5)) {
        throw new Error('Device temperature out of range. Please wait for device to stabilize.');
      }

      /// No Firmware version check required
      // const fwVersion = parseFloat(health.firmwareVersion);
      // if (!isNaN(fwVersion) && fwVersion > 0 && fwVersion < 2) {
      //   throw new Error('Firmware update required. Please update to version 2.x or higher.');
      // }

      // Health check passed
      setDeviceHealth(health);
      setConnectionStatus('connected');

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';

      if (errorMessage.includes('No device found') || errorMessage.includes('No device selected')) {
        setError('no-device-found', errorMessage);
      } else if (errorMessage.includes('Battery') || errorMessage.includes('temperature') || errorMessage.includes('Firmware')) {
        setError('health-check-failed', errorMessage);
      } else {
        setError('connection-failed', errorMessage);
      }
    }
  };

  // Select-then-effect, for BOTH transports: clicking a button only calls
  // selectConnectionType(...). App.tsx re-renders with the matching transport prop,
  // DeviceServiceProvider's memo swaps in the matching service instance, and this effect
  // (watching connectionType and the freshly-swapped deviceService) performs the actual
  // connect once the correct instance is available. The device picker prompt therefore
  // appears one render-tick after the click rather than synchronously with it — accepted
  // per the story spec.
  useEffect(() => {
    if (!connectionType) {
      handledForTypeRef.current = null;
      return;
    }
    if (handledForTypeRef.current === connectionType) {
      return;
    }
    handledForTypeRef.current = connectionType;
    performConnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionType, deviceService]);

  const isSelecting = connectionStatus === 'selecting';

  return (
    <Dialog 
      open={isOpen && isSelecting} 
      onClose={close}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: 2,
        }
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 3 }}>
        <Typography variant="h6" component="div">
          Choose the connection type:
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box 
          display="flex" 
          gap={3} 
          justifyContent="center" 
          alignItems="stretch"
          sx={{ mb: 3 }}
        >
          <Box sx={{ flex: 1 }}>
            <Button
              variant="outlined"
              size="large"
              fullWidth
              onClick={() => selectConnectionType('bluetooth')}
              sx={{
                height: '120px',
                flexDirection: 'column',
                gap: 1,
                borderRadius: 2,
              }}
            >
              <BluetoothIcon sx={{ fontSize: 48 }} />
              <Typography variant="button">Bluetooth</Typography>
            </Button>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={() => selectConnectionType('usb')}
              sx={{
                height: '120px',
                flexDirection: 'column',
                gap: 1,
                borderRadius: 2,
              }}
            >
              <UsbIcon sx={{ fontSize: 48 }} />
              <Typography variant="button">Cable</Typography>
            </Button>
          </Box>
        </Box>

        <Box display="flex" justifyContent="center">
          <Button 
            variant="text" 
            onClick={close}
            sx={{ minWidth: 100 }}
          >
            Cancel
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
